import { captureException, captureMessage } from '@sentry/react';
import { WebSocketMessage } from 'generated/graphql';
import { useCallback, useEffect, useRef, useState } from 'react';
import AuthService from 'services/AuthService';
import { useGetUser } from 'v2/core/services/user';

const NORMAL_WS_CLOSURE_CODE = 1000;

interface createWebSocketConnectionParams {
  withSessionID?: string;
  skipCloseExistingConnection?: boolean;
}

export function useCreateWebSocketConnection() {
  const {
    id: userID,
    tenant: { slug: tenantSlug },
  } = useGetUser();

  const [sessionID, setSessionID] = useState<string | null>(null);
  const [webSocket, setWebSocket] = useState<WebSocket | null>(null);
  const [responseID, setResponseID] = useState<string | undefined>(undefined);
  const deps = { tenantSlug, webSocket, sessionID, responseID };
  const depsRef = useRef(deps);
  depsRef.current = deps;

  const closeWebSocketConnection = useCallback((ws: WebSocket | null) => {
    if (!ws) {
      return;
    }

    if (ws.readyState === ws.OPEN) {
      ws.close(NORMAL_WS_CLOSURE_CODE);
    } else {
      ws.addEventListener('open', () => {
        ws.close(NORMAL_WS_CLOSURE_CODE);
      });
    }

    setSessionID(null);
    setWebSocket(null);
    setResponseID(undefined);
  }, []);

  const createWebSocketConnection = useCallback(
    ({
      withSessionID,
      skipCloseExistingConnection = false,
    }: createWebSocketConnectionParams = {}) => {
      console.log('New Websocket create request initiated');

      if (!process.env.REACT_APP_WEBSOCKET_URL) {
        return;
      }

      if (!skipCloseExistingConnection) {
        closeWebSocketConnection(depsRef.current.webSocket);
      }

      try {
        const queryParams = `?authToken=${AuthService.getToken()}${
          depsRef.current.tenantSlug
            ? `&slug=${encodeURIComponent(depsRef.current.tenantSlug)}`
            : ''
        }`;

        const newWebSocket = new WebSocket(
          `${process.env.REACT_APP_WEBSOCKET_URL}${queryParams}`,
        );

        console.log(
          'New Websocket connection created:',
          JSON.stringify({
            slug: encodeURIComponent(depsRef.current.tenantSlug || ''),
            sessionID: depsRef.current.sessionID,
            responseID: depsRef.current.responseID,
          }),
        );

        if (withSessionID) {
          setSessionID(withSessionID);
        }
        setWebSocket(newWebSocket);

        return () => closeWebSocketConnection(newWebSocket);
      } catch (error) {
        captureException({
          error,
          message: 'Failed to create WebSocket',
        });
      }
    },
    [closeWebSocketConnection],
  );

  useEffect(() => {
    if (!webSocket) {
      return;
    }

    const currentSessionID = depsRef.current.sessionID;

    const requestHeaders = {
      authToken: AuthService.getToken(),
      createSession: currentSessionID ? undefined : { userID },
      resumeSession: currentSessionID
        ? { userID, sessionID: currentSessionID }
        : undefined,
    };

    webSocket.onopen = () => {
      webSocket.send(
        JSON.stringify(
          tenantSlug ? { ...requestHeaders, slug: tenantSlug } : requestHeaders,
        ),
      );
    };

    webSocket.addEventListener('message', (message) => {
      if (message.data) {
        const data: WebSocketMessage = JSON.parse(message.data);
        const sessionID = data?.createSession?.sessionID ?? data.sessionID;
        const responseID = data.responseID;
        sessionID && setSessionID(sessionID);
        responseID && setResponseID(responseID);
      }
    });

    webSocket.addEventListener('close', (event) => {
      console.log(
        'Websocket closed:',
        JSON.stringify({
          code: event.code,
          reason: event.reason,
          sessionID: depsRef.current.sessionID,
          responseID: depsRef.current.responseID,
        }),
      );

      // Anything other than 1000 code is an abnormal closure & we should try to resume
      // Ref: https://developer.mozilla.org/en-US/docs/Web/API/CloseEvent/code
      if (event.code !== NORMAL_WS_CLOSURE_CODE) {
        createWebSocketConnection({ skipCloseExistingConnection: true });
        captureMessage('Websocket connection closed abnormally', {
          extra: {
            code: event.code,
            reason: event.reason,
            sessionID: depsRef.current.sessionID,
            responseID: depsRef.current.responseID,
          },
          level: 'info',
        });
      }
    });
  }, [webSocket, userID, tenantSlug, createWebSocketConnection]);

  useEffect(() => {
    return () => {
      closeWebSocketConnection(depsRef.current.webSocket);
    };
  }, [closeWebSocketConnection]);

  return [
    createWebSocketConnection,
    {
      sessionID,
      webSocket,
      responseID,
      isLoading: webSocket ? !sessionID : false,
      closeWebSocketConnection,
    },
  ] as const;
}
